import { motion } from "framer-motion";
import { Star } from "lucide-react";
import { SITE } from "@/data/site";

// Google Reviews widget — visual social-proof card. Rating & count are shown from
// GOOGLE_REVIEWS below and linked to the live Google reviews page. Update the
// two numbers when the resort's public Google rating changes.
export const GOOGLE_REVIEWS = {
  rating: 4.9,
  count: 120,
};

const GLogo = ({ className = "h-5 w-5" }) => (
  <svg className={className} viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

const StarBar = ({ rating }) => {
  const full = Math.floor(rating);
  const half = rating - full >= 0.4 && rating - full < 0.9;
  return (
    <div className="flex items-center gap-1">
      {Array.from({ length: 5 }).map((_, i) => {
        const active = i < full;
        const isHalf = i === full && half;
        return (
          <div key={i} className="relative">
            <Star className="h-6 w-6 text-charcoal/15" />
            {(active || isHalf) && (
              <div
                className="absolute inset-0 overflow-hidden"
                style={{ width: isHalf ? "50%" : "100%" }}
              >
                <Star className="h-6 w-6 fill-[#F5B400] text-[#F5B400]" />
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default function GoogleReviewsWidget() {
  const { rating, count } = GOOGLE_REVIEWS;
  const distributionBars = [95, 4, 1, 0, 0]; // approximate 5→1 star distribution

  return (
    <section data-testid="google-reviews-widget" className="bg-ivory py-16 md:py-20">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-4xl overflow-hidden rounded-3xl border border-charcoal/10 bg-card shadow-[0_20px_60px_rgba(26,54,38,0.08)]"
        >
          {/* Header */}
          <div className="flex flex-col items-start gap-6 border-b border-charcoal/8 p-7 sm:flex-row sm:items-center sm:justify-between md:p-9">
            <div className="flex items-center gap-3.5">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-ivory shadow-[0_4px_12px_rgba(0,0,0,0.08)] md:h-12 md:w-12">
                <GLogo className="h-6 w-6 md:h-7 md:w-7" />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest-2 text-brown md:text-sm">Google Reviews</p>
                <p className="mt-1 font-display text-xl font-bold text-forest md:text-2xl">Vruksha Valley Eco Resort</p>
              </div>
            </div>

            <a
              href={SITE.googleReviews}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="google-widget-write-btn"
              className="group inline-flex items-center gap-2 rounded-full bg-forest px-6 py-3 text-sm font-medium text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-moss"
            >
              <Star className="h-4 w-4 fill-current" />
              Write a review
            </a>
          </div>

          {/* Body */}
          <div className="grid gap-8 p-7 md:grid-cols-[auto_1fr] md:gap-14 md:p-10">
            {/* Score */}
            <div className="flex flex-col items-start md:min-w-[220px] md:border-r md:border-charcoal/8 md:pr-10">
              <div className="flex items-baseline gap-2">
                <p className="font-display text-6xl font-bold leading-none text-forest md:text-7xl">
                  {rating.toFixed(1)}
                </p>
                <p className="text-xl text-charcoal/50">/ 5</p>
              </div>
              <div className="mt-4">
                <StarBar rating={rating} />
              </div>
              <p className="mt-3 flex items-center gap-2 text-sm text-charcoal/70">
                <span className="relative flex h-2 w-2">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#F5B400] opacity-70" />
                  <span className="relative inline-flex h-2 w-2 rounded-full bg-[#F5B400]" />
                </span>
                Based on <span className="font-semibold text-forest">{count}+</span> Google Reviews
              </p>
            </div>

            {/* Distribution */}
            <div className="flex flex-col justify-center gap-2.5">
              {distributionBars.map((pct, i) => {
                const label = 5 - i;
                return (
                  <div key={label} className="flex items-center gap-3">
                    <span className="w-6 text-sm text-charcoal/60">{label}★</span>
                    <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-charcoal/8">
                      <motion.span
                        initial={{ width: 0 }}
                        whileInView={{ width: `${pct}%` }}
                        viewport={{ once: true }}
                        transition={{ duration: 1.1, delay: 0.15 + i * 0.08, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute inset-y-0 left-0 rounded-full"
                        style={{
                          background:
                            "linear-gradient(90deg, #F5B400 0%, #FFCC33 100%)",
                        }}
                      />
                    </div>
                    <span className="w-10 text-right text-sm tabular-nums text-charcoal/60">{pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <a
            href={SITE.googleReviews}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-widget-view-all"
            className="group flex items-center justify-center gap-2 border-t border-charcoal/8 bg-ivory/60 py-4 text-sm font-medium text-forest transition-colors duration-300 hover:bg-ivory"
          >
            View all reviews on Google
            <span className="transition-transform duration-300 group-hover:translate-x-0.5">→</span>
          </a>
        </motion.div>
      </div>
    </section>
  );
}
